import {datadogRum} from '@datadog/browser-rum';
import {useEffect} from 'react';

import {assert} from 'Asserts';
import {distribution} from 'metrics/Metrics';
import {getCurrentTime} from 'performance/getCurrentTime';
import {processAnalysesEvents} from 'performance/processAnalysesEvents';
import {processGraphQLEvents} from 'performance/processGraphQLEvents';
import {processInventoryEvents} from 'performance/processInventoryEvents';
import {processMolbioEvents} from 'performance/processMolbioEvents';
import {processNavigationEvents} from 'performance/processNavigationEvents';
import {processNotebookEvents} from 'performance/processNotebookEvents';
import {processProceduresEvents} from 'performance/processProceduresEvents';
import {processRegistryEvents} from 'performance/processRegistryEvents';
import {processSearchEvents} from 'performance/processSearchEvents';
import {processWorksheetsEvents} from 'performance/processWorksheetsEvents';
import {
  EventMetadata,
  EventTags,
  ProfilerEvent,
  ProfilerEventDomain,
  ProfilerEventValue,
} from 'performance/ProfilerConstants';

import {isInAcceptableDatadogTagFormat, toSeconds} from './PerformanceUtils';
import {processLabAutomationEvents} from './processLabAutomationEvents';
import {processProcessesEvents} from './processProcessesEvents';
import {processRequestsV2Events} from './processRequestsV2Events';
import {processResultsEvents} from './processResultsEvents';
import {processReviewsEvents} from './processReviewsEvents';
import {processSampleTestsEvents} from './processSampleTestsEvents';
import {processStudiesEvents} from './processStudiesEvents';

const EVENT_ACTION_TO_PROCESSING_FUNCTION: {[key in ProfilerEventDomain]: Function} = {
  [ProfilerEventDomain.ANALYSES]: processAnalysesEvents,
  [ProfilerEventDomain.GRAPHQL]: processGraphQLEvents,
  [ProfilerEventDomain.INVENTORY]: processInventoryEvents,
  [ProfilerEventDomain.LAB_AUTOMATION]: processLabAutomationEvents,
  [ProfilerEventDomain.MOLBIO]: processMolbioEvents,
  [ProfilerEventDomain.NAVIGATION]: processNavigationEvents,
  [ProfilerEventDomain.NOTEBOOK]: processNotebookEvents,
  [ProfilerEventDomain.PROCEDURES]: processProceduresEvents,
  [ProfilerEventDomain.PROCESSES]: processProcessesEvents,
  [ProfilerEventDomain.REGISTRY]: processRegistryEvents,
  [ProfilerEventDomain.REQUESTS_V2]: processRequestsV2Events,
  [ProfilerEventDomain.RESULTS]: processResultsEvents,
  [ProfilerEventDomain.REVIEWS]: processReviewsEvents,
  [ProfilerEventDomain.SAMPLE_TESTS]: processSampleTestsEvents,
  [ProfilerEventDomain.SEARCH]: processSearchEvents,
  [ProfilerEventDomain.STUDIES]: processStudiesEvents,
  [ProfilerEventDomain.WORKSHEETS]: processWorksheetsEvents,
};

export interface ProfilerEventState {
  [eventKey: string]: ProfilerEventValue;
}

const profilerEventState: ProfilerEventState = {};
const idsByEventKey: {[eventBaseKey: string]: Array<string>} = {};

export function resetBenchlingProfilerStateForTests(): void {
  for (const key of Object.keys(profilerEventState)) {
    delete profilerEventState[key];
  }
  for (const key of Object.keys(idsByEventKey)) {
    delete idsByEventKey[key];
  }
}

function getBaseKeyForProfilerEvent({domain, feature, action}: ProfilerEvent): string {
  return `${domain}.${feature}.${action}`;
}
export function getKeyForProfilerEvent(event: ProfilerEvent): string {
  const baseKey = getBaseKeyForProfilerEvent(event);
  // Generate key with ID if provided, otherwise generate base key
  return event.id ? `${baseKey}.${event.id}` : baseKey;
}

export function getProfilerEventValue(event: ProfilerEvent): ProfilerEventValue {
  const keyOrBaseKey = getKeyForProfilerEvent(event);
  const state = profilerEventState[keyOrBaseKey];
  if (!state && !event.id) {
    // If state was not found and id was not provided, then this is a base key and we look for the first
    // event with the same base key
    const id = idsByEventKey[keyOrBaseKey]?.[0];
    if (id) {
      return profilerEventState[getKeyForProfilerEvent({...event, id})];
    }
  }
  return state;
}

export function logProfilerTimingEvent(event: ProfilerEvent, extra?: Record<string, unknown>): void {
  profilerEventState[getKeyForProfilerEvent(event)] = {
    timestamp: getCurrentTime(),
    extra,
    id: event.id,
  };
  if (event.id) {
    // If id is provided, add it to the list of ids currently in-flight for that event
    const baseKey = getBaseKeyForProfilerEvent(event);
    if (!idsByEventKey[baseKey]) {
      idsByEventKey[baseKey] = [];
    }
    idsByEventKey[baseKey].push(event.id);
  }
  EVENT_ACTION_TO_PROCESSING_FUNCTION[event.domain](event);
}

export function clearProfilerTimingEvent(event: ProfilerEvent): void {
  delete profilerEventState[getKeyForProfilerEvent(event)];
  if (event.id) {
    // If an id was also provided, we also need to remove it from the list of ids for that event
    const baseKey = getBaseKeyForProfilerEvent(event);
    const ids = idsByEventKey[baseKey];
    if (ids) {
      const idIndex = ids?.indexOf(event.id);
      ids.splice(idIndex, 1);
      idsByEventKey[baseKey] = ids;
    }
  }
}

export function clearAllTimingEventsWithDomainAndFeature(domain: ProfilerEventDomain, feature: string): void {
  const prefix = `${domain}.${feature}`;
  Object.keys(profilerEventState).forEach((key) => {
    if (key.startsWith(prefix)) {
      delete profilerEventState[key];
    }
  });
  Object.keys(idsByEventKey).forEach((key) => {
    if (key.startsWith(prefix)) {
      delete idsByEventKey[key];
    }
  });
}

export function measureTimingBetweenEvents(
  startEvent: ProfilerEvent,
  endEvent: ProfilerEvent
): void | {
  time: number;
  startEvent: ProfilerEventValue;
  endEvent: ProfilerEventValue;
} {
  const startEventValue = getProfilerEventValue(startEvent);
  const endEventValue = getProfilerEventValue({...endEvent, id: startEventValue?.id});

  if (startEventValue != null && endEventValue != null) {
    assert(startEventValue.id === endEventValue.id, 'Profiler start/end event ID mismatch');

    const timeToFetch = toSeconds(endEventValue.timestamp - startEventValue.timestamp);
    if (timeToFetch < 0) {
      // If the time is negative, there was a leftover rendered event timing from a previous search
      // This clears the state, and doesn't send the metric
      clearProfilerTimingEvent({...endEvent, id: endEventValue.id});
      return;
    }

    clearProfilerTimingEvent({...startEvent, id: startEventValue.id});
    clearProfilerTimingEvent({...endEvent, id: endEventValue.id});

    return {time: timeToFetch, startEvent: startEventValue, endEvent: endEventValue};
  }
}

/**
 * Emits an action to datadog with the target event version
 */
function addDatadogAction(name: string, context: object, eventVersion: number): void {
  datadogRum.addAction(name, {...context, versionTag: eventVersion});
}

const EMPTY_RECORD: Record<string, unknown> = {};
/**
 * Generic function to process an event, where the metadata exists on the "end" profilerEvent.
 * The "end" profilerEvent's extra field should be of type EventMetadata.
 *
 * @param startProfilerEvent The start event
 * @param endProfilerEvent The end event
 * @param actionName The name of the action
 * @param distributionName The name of the distribution
 * @param eventVersion The latest version number for the event
 */
export function processTimingEvent(
  startProfilerEvent: ProfilerEvent,
  endProfilerEvent: ProfilerEvent,
  actionName: string,
  distributionName: string,
  eventVersion: number
): void {
  const measurement = measureTimingBetweenEvents(startProfilerEvent, endProfilerEvent);
  if (measurement == null) {
    return;
  }
  const {time, endEvent} = measurement;
  const eventMetadata: EventMetadata | undefined = endEvent?.extra as EventMetadata;

  const actionMetadata: EventTags = eventMetadata?.actionMetadata || EMPTY_RECORD;
  const distributionMetadata: EventTags = eventMetadata?.distributionMetadata || EMPTY_RECORD;

  assert(isInAcceptableDatadogTagFormat(actionMetadata), 'actionMetadata is not in acceptable format');
  assert(
    isInAcceptableDatadogTagFormat(distributionMetadata),
    'distributionMetadata is not in acceptable format'
  );

  addDatadogAction(
    actionName,
    actionMetadata !== EMPTY_RECORD ? {time, metadata: actionMetadata} : {time},
    eventVersion
  );
  distribution(distributionName, time, distributionMetadata);
}

interface BenchlingProfilerProps extends ProfilerEvent {
  children: JSX.Element;
  extra?: Record<string, unknown>;
}

export default function BenchlingProfiler(props: BenchlingProfilerProps): JSX.Element {
  useEffect(() => {
    logProfilerTimingEvent(props, props.extra);
  });

  return props.children;
}
